import React, { useState, useEffect, useRef } from 'react';
import { Search, UserPlus, ThermometerSun, ThermometerSnowflake, Edit2, X, CheckSquare, Bell, Upload, FileText } from 'lucide-react';
import AddContactModal from './AddContactModal';
import EditContactModal from './EditContactModal';
import ContactDetails from './ContactDetails';
import { Contact } from '../../types';
import { api } from '../../services/api';
import { getTimeAgo } from '../../utils/contactUtils';
import { reminderService } from '../../services/reminderService';
import ContactImporter from './ContactImporter';
import { useSubscription } from '../../contexts/SubscriptionContext';
import FeatureGate from '../subscription/FeatureGate';
import UsageIndicator from '../subscription/UsageIndicator';
import UpgradePrompt from '../subscription/UpgradePrompt';


const CONTACTS_PER_PAGE = 20; // Number of contacts to show per page

const ContactList: React.FC = () => {
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [editingContact, setEditingContact] = useState<Contact | null>(null);
  const [selectedContact, setSelectedContact] = useState<Contact | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterWarmth, setFilterWarmth] = useState<'all' | 'warm' | 'cold'>('all');
  const [filterGroup, setFilterGroup] = useState<string | null>(null);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [groups, setGroups] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [userSettings, setUserSettings] = useState({
    coldThresholdDays: 30 // default value
  });
  const [reminderCounts, setReminderCounts] = useState<Record<number, number>>({});
  const [showImporter, setShowImporter] = useState(false);
  const { usageStats, setShowUpgradeModal } = useSubscription();
  
  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [hasInitialDataLoaded, setHasInitialDataLoaded] = useState(false);

  useEffect(() => {
    Promise.all([
      fetchContacts(),
      fetchGroups(),
      fetchUserSettings()
    ]).then(() => {
      setLoading(false);
    }).catch(error => {
      console.error('Error fetching data:', error);
      setLoading(false);
    });
  }, []);

  // Effect to fetch reminders for all contacts
  useEffect(() => {
    if (contacts.length > 0) {
      fetchReminderCounts();
    }
  }, [contacts]);

  const fetchUserSettings = async () => {
    try {
      const settings = await api.getUserSettings();
      setUserSettings(settings);
    } catch (error) {
      console.error('Failed to fetch user settings:', error);
    }
  };

  const fetchContacts = async () => {
    try {
      const data = await api.getContacts();
      setContacts(data);
    } catch (error) {
      console.error('Failed to fetch contacts:', error);
    }
  };

  const fetchGroups = async () => {
    try {
      const groupsData = await api.getGroups();
      setGroups(groupsData.map((group: any) => group.name));
    } catch (error) {
      console.error('Failed to fetch groups:', error);
    }
  };

  // Optimized function to fetch reminder counts for all contacts at once
  // We'll only fetch counts for contacts that are currently visible on the page
  // plus a buffer of contacts for smooth pagination 
  const fetchReminderCounts = async () => {
    try {
      if (contacts.length === 0) return;
      
      // Create a set of contact IDs to fetch reminders for
      const contactIdsToFetch = new Set<number>();
      
      // Always include currently visible contacts
      paginatedContacts.forEach(contact => {
        if (contact.email) {
          contactIdsToFetch.add(contact.id);
        }
      });
      
      // Add contacts from the next page (if any) to preload data
      const nextPageContacts = filteredContacts.slice(endIndex, endIndex + CONTACTS_PER_PAGE);
      nextPageContacts.forEach(contact => {
        if (contact.email) {
          contactIdsToFetch.add(contact.id);
        }
      });
      
      // Convert to array
      const contactIds = Array.from(contactIdsToFetch);
      
      if (contactIds.length === 0) return;
      
      // Use the batch API to get reminder counts for visible contacts + next page
      const counts = await api.getBatchReminderCounts(contactIds);
      
      // Update the counts map - keep existing counts for contacts not in the current fetch
      setReminderCounts(prevCounts => {
        const newCounts = { ...prevCounts };
        contactIds.forEach(id => {
          newCounts[id] = counts[id] || 0;
        });
        return newCounts;
      });
    } catch (error) {
      console.error('Failed to fetch reminder counts:', error);
      
      // Fallback to individual fetches if the batch API fails, but only for visible contacts
      const visibleContactIds = paginatedContacts
        .filter(contact => contact.email)
        .map(contact => contact.id);
        
      const fallbackCounts: Record<number, number> = {};
      for (const id of visibleContactIds) {
        try {
          const reminders = await reminderService.getRemindersForContact(id);
          fallbackCounts[id] = reminders.filter(r => r.status === 'pending').length;
        } catch (e) {
          fallbackCounts[id] = 0;
        }
      }
      
      // Update only the fetched contacts' counts
      setReminderCounts(prevCounts => ({...prevCounts, ...fallbackCounts}));
    }
  };
  
  // Trigger reminder fetch when the page changes
  useEffect(() => {
    fetchReminderCounts();
  }, [currentPage]);

  const handleAddContact = async (contactData: Omit<Contact, 'id'>) => {
    try {
      console.log('Creating contact with data:', contactData);
      const newContact = await api.createContact(contactData);
      console.log('API response:', newContact);
      
      if (newContact && newContact.id) {
        setContacts(prevContacts => [...prevContacts, newContact]);
        console.log('Updated contacts state');
        
        // Check if new groups were created and refresh the groups list
        if (contactData.groups && contactData.groups.length > 0) {
          // Find any groups that weren't in our existing list
          const newGroups = contactData.groups.filter(g => !groups.includes(g));
          if (newGroups.length > 0) {
            console.log(`New groups detected: ${newGroups.join(', ')}. Refreshing groups list.`);
            fetchGroups().catch(err => console.error('Failed to refresh groups after contact creation:', err));
          }
        }
      } else {
        console.error('Invalid contact returned from API:', newContact);
      }
    } catch (error) {
      console.error('Failed to create contact:', error);
    }
  };

  const handleUpdateContact = async (contactId: number, contactData: Partial<Contact>) => {
    console.log('[ContactList.handleUpdateContact] Called with:', { contactId, contactData });
    try {
      const updatedContact = await api.updateContact(contactId, contactData);
      console.log('[ContactList.handleUpdateContact] Received updatedContact from API:', updatedContact);
      setContacts(contacts.map(c => 
        c.id === contactId ? updatedContact : c
      ));
      
      // Check if groups were updated (either through adding or removing)
      if (contactData.groups && Array.isArray(contactData.groups)) {
        // Refresh the groups list to ensure the UI has latest data
        fetchGroups().catch(err => console.error('Failed to refresh groups after contact update:', err));
      }
      
      // Refresh reminders count for this contact
      if (contactId) {
        try {
          const reminders = await reminderService.getRemindersForContact(contactId);
          const pendingCount = reminders.filter(r => r.status === 'pending').length;
          setReminderCounts(prev => ({...prev, [contactId]: pendingCount}));
        } catch (reminderError) {
          console.warn(`Failed to refresh reminders for contact ${contactId}:`, reminderError);
        }
      }
      
      return updatedContact;
    } catch (error) {
      console.error('Failed to update contact:', error);
      return null;
    }
  };

  // Handler for when a Todo is added/updated/deleted in ContactDetails
  const handleTodoChange = async (contactId: number) => {
    console.log(`[ContactList.handleTodoChange] TODOs changed for contactId: ${contactId}. Refetching...`);
    try {
      // Refetch the specific contact from the API to get the latest todos
      const refreshedContact = await api.getContact(contactId); 
      
      // Update the main contacts list state
      setContacts(prevContacts => 
        prevContacts.map(c => (c.id === contactId ? refreshedContact : c))
      );
      
      // Update the selected contact state if it's the one currently open
      if (selectedContact && selectedContact.id === contactId) {
        setSelectedContact(refreshedContact);
      }
    } catch (error) {
      console.error(`[ContactList.handleTodoChange] Failed to refetch contact ${contactId}:`, error);
      // Optionally show an error message to the user
    }
  };

  const handleDeleteContact = async (contactId: number) => {
    if (!window.confirm('Are you sure you want to delete this contact?')) {
      return;
    }
    
    try {
      console.log('Attempting to delete contact:', contactId);
      
      // Direct fetch call to avoid any potential issues in the API service
      const response = await fetch(`/api/contacts/${contactId}`, {
        method: 'DELETE',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      // Check for errors and handle them appropriately
      if (!response.ok) {
        const errorText = await response.text().catch(() => 'Unknown error');
        console.error('Delete error response:', response.status, errorText);
        
        if (response.status === 500) {
          // Special handling for server errors
          alert('Server error when deleting contact. This contact may have related data that prevents deletion.');
          return;
        }
        
        throw new Error(`Failed to delete contact: ${response.status}`);
      }
      
      console.log('Contact deleted successfully');
      
      // Update the contacts list by removing the deleted contact
      setContacts(prev => prev.filter(c => c.id !== contactId));
      
      // Remove reminder count for this contact
      setReminderCounts(prev => {
        const newCounts = {...prev};
        delete newCounts[contactId];
        return newCounts;
      });
      
      // If the currently selected contact is being deleted, close the detail view
      if (selectedContact?.id === contactId) {
        setSelectedContact(null);
      }
    } catch (error) {
      console.error('Delete error:', error);
      alert('Failed to delete contact. Please try again.');
    }
  };

  // We'll still apply filters to the full contact list
  // This ensures compatibility with other features that expect filtering to work
  const filteredContacts = contacts.filter(contact => {
    const matchesSearch = searchQuery.toLowerCase().trim() === '' ||
      contact.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      contact.groups.some(group => group.toLowerCase().includes(searchQuery.toLowerCase())) ||
      (contact.notes || '').toLowerCase().includes(searchQuery.toLowerCase());

    const matchesWarmth = filterWarmth === 'all' || contact.warmth === filterWarmth;
    
    const matchesGroup = !filterGroup || contact.groups.includes(filterGroup);

    return matchesSearch && matchesWarmth && matchesGroup;
  });
  
  // Calculate pagination - we'll still use client-side pagination
  // This avoids breaking other components that need access to all contacts
  const totalPages = Math.ceil(filteredContacts.length / CONTACTS_PER_PAGE);
  const startIndex = (currentPage - 1) * CONTACTS_PER_PAGE;
  const endIndex = startIndex + CONTACTS_PER_PAGE;
  const paginatedContacts = filteredContacts.slice(startIndex, endIndex);
  
  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, filterWarmth, filterGroup]);
  
  // This reference will hold the complete set of contacts for other components
  // Even though we paginate the display, we keep the full list in memory
  const allContacts = useRef<Contact[]>([]);
  
  // Update the reference whenever contacts changes
  useEffect(() => {
    allContacts.current = contacts;
    if (contacts.length > 0 && !hasInitialDataLoaded) {
      setHasInitialDataLoaded(true);
    }
  }, [contacts, hasInitialDataLoaded]);

  const formatDate = (dateString: string) => {
    if (!dateString) return 'Never';
    
    const date = new Date(dateString);
    // Check if the date is valid
    if (isNaN(date.getTime())) return 'Invalid date';
    
    // Format as YYYY-MM-DD
    return date.toISOString().split('T')[0];
    
    // Or use toLocaleDateString for localized format
    // return date.toLocaleDateString();
  };

  if (loading) {
    return <div className="flex justify-center items-center h-64">
      <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-500"></div>
    </div>;
  }

  return (
    <div className="max-w-4xl mx-auto p-4">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold">My Inner Circle</h1>
        <div className="flex gap-2">
          <button 
            onClick={() => setShowImporter(!showImporter)}
            className="flex items-center gap-2 bg-white text-gray-700 px-4 py-2 rounded-lg border border-gray-300 hover:bg-gray-50 transition-colors text-sm font-medium"
          >
            <Upload size={18} />
            <span>Import Contacts</span>
          </button>
          <FeatureGate
            feature="contacts"
            currentUsage={usageStats?.contacts.count}
            limit={usageStats?.contacts.limit}
            showUpgradeButton={true}
          >
            <button 
              onClick={() => setIsAddModalOpen(true)}
              className="flex items-center gap-2 bg-brand-primary text-white px-4 py-2 rounded-lg hover:bg-indigo-700 transition-colors text-sm font-medium shadow-sm"
            >
              <UserPlus size={18} />
              <span>Add Contact</span>
            </button>
          </FeatureGate>
        </div>
      </div>

      {usageStats && (
        <UsageIndicator
          current={usageStats.contacts.count}
          limit={usageStats.contacts.limit}
          feature="Contacts Usage"
          className="mb-4"
        />
      )}

      {usageStats && usageStats.contacts.percentage >= 80 && (
        <UpgradePrompt
          type="contacts"
          currentPlan={usageStats.subscription.plan as 'free' | 'medium'}
          percentageUsed={usageStats.contacts.percentage}
          onUpgrade={() => setShowUpgradeModal(true)}
          onDismiss={() => {}} // Handle dismissal logic
          className="mb-4"
        />
      )}

      {showImporter && (
        <div className="mb-6">
          <ContactImporter />
        </div>
      )}

      <div className="space-y-4 mb-6">
        <div className="relative">
          <Search className="absolute left-3 top-3 text-gray-400" size={20} />
          <input
            type="text"
            placeholder="Search contacts, groups, or notes..."
            className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>

        <div className="flex gap-2">
          <button
            onClick={() => setFilterWarmth('all')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-150 ${
              filterWarmth === 'all'
                ? 'bg-gray-100 text-gray-800 border border-gray-300'
                : 'bg-white text-gray-700 border border-gray-200 hover:bg-gray-50 hover:border-gray-300'
            }`}
          >
            All
          </button>
          <button
            onClick={() => setFilterWarmth('warm')}
            className={`px-4 py-2 rounded-lg flex items-center gap-1.5 text-sm font-medium transition-colors duration-150 ${
              filterWarmth === 'warm'
                ? 'bg-tag-yellow-bg text-tag-yellow-text border border-yellow-300'
                : 'bg-white text-gray-700 border border-gray-200 hover:bg-gray-50 hover:border-gray-300'
            }`}
          >
            <ThermometerSun size={16} />
            Warm
          </button>
          <button
            onClick={() => setFilterWarmth('cold')}
            className={`px-4 py-2 rounded-lg flex items-center gap-1.5 text-sm font-medium transition-colors duration-150 ${
              filterWarmth === 'cold'
                ? 'bg-tag-blue-bg text-tag-blue-text border border-blue-300'
                : 'bg-white text-gray-700 border border-gray-200 hover:bg-gray-50 hover:border-gray-300'
            }`}
          >
            <ThermometerSnowflake size={16} />
            Cold
          </button>
        </div>

        <div className="flex items-center gap-2">
          <label className="text-sm font-medium text-gray-700">
            Filter by group:
          </label>
          <select
            value={filterGroup || ''}
            onChange={(e) => setFilterGroup(e.target.value || null)}
            className="px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            <option value="">All Groups</option>
            {Array.from(new Set(contacts.flatMap(c => c.groups))).sort().map(group => (
              <option key={group} value={group}>
                {group}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="space-y-4">
        {filteredContacts.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-gray-500 text-lg">No contacts found</p>
            <p className="text-gray-400 text-sm mt-2">Try adjusting your search or filters</p>
          </div>
        ) : (
          <>
            <div className="flex justify-between mb-2 items-center">
              <p className="text-gray-600 text-sm">
                Showing {startIndex + 1} to {Math.min(endIndex, filteredContacts.length)} of {filteredContacts.length} contacts
              </p>
            </div>
            
            {/* Paginated contacts list - Updated Styling */}
            <div className="bg-white rounded-lg border border-brand-muted divide-y divide-brand-muted">
              {paginatedContacts.map(contact => (
                <div 
                  key={contact.id} 
                  className="flex items-center p-4 hover:bg-brand-muted/50 transition-colors cursor-pointer"
                  onClick={() => setSelectedContact(contact)}
                >
                  {/* Column 1: Name & Groups (Flexible Width) */}
                  <div className="flex-1 min-w-0 pr-4">
                    <h3 className="text-base font-medium text-gray-900 truncate">{contact.name}</h3>
                    <div className="mt-1 flex flex-wrap gap-1">
                      {contact.groups.map(group => (
                        <span 
                          key={group} 
                          className="bg-tag-blue-bg text-tag-blue-text px-2 py-0.5 rounded-full text-xs font-medium"
                        >
                          {group}
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* Column 2: Last Interaction & Warmth (Hide on small screens) */}
                  <div className="hidden md:block flex-shrink-0 md:w-48 px-4 text-sm text-gray-600">
                    <div>Last interaction: {getTimeAgo(contact.lastInteraction)}</div>
                    {contact.warmth === 'cold' && (
                      <span className="inline-flex items-center mt-1 px-2 py-0.5 rounded-full text-xs font-medium bg-tag-blue-bg text-tag-blue-text">
                         <ThermometerSnowflake className="mr-1" size={12} /> Cold
                      </span>
                    )}
                    {contact.warmth === 'warm' && (
                       <span className="inline-flex items-center mt-1 px-2 py-0.5 rounded-full text-xs font-medium bg-tag-yellow-bg text-tag-yellow-text">
                         <ThermometerSun className="mr-1" size={12} /> Warm
                       </span>
                    )}
                  </div>

                  {/* Column 3: Todos & Reminders (Hide on small screens) */}
                  <div className="hidden md:block flex-shrink-0 md:w-40 px-4 text-sm text-gray-600">
                     <div className="flex items-center gap-1.5">
                       <CheckSquare size={14} />
                       <span>{contact.todos.filter(todo => !todo.completed).length} Todos</span>
                     </div>
                  </div>

                  {/* Column 4: Actions (Adjust width slightly) */}
                  <div className="flex-shrink-0 w-20 flex items-center justify-end gap-1 pl-2 md:pl-4">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        if (window.confirm('Are you sure you want to delete this contact?')) {
                          handleDeleteContact(contact.id);
                        }
                      }}
                      className="p-1.5 hover:bg-red-100 rounded-full text-gray-400 hover:text-red-500 transition-colors"
                      title="Delete Contact"
                    >
                      <X size={18} />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setEditingContact(contact);
                      }}
                      className="p-1.5 hover:bg-gray-100 rounded-full text-gray-400 hover:text-gray-600 transition-colors"
                      title="Edit Contact"
                    >
                      <Edit2 size={18} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
            
            {/* Pagination controls */}
            {totalPages > 1 && (
              <div className="flex justify-center items-center mt-6 gap-2">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                  className={`px-3 py-1 rounded-md border text-sm font-medium transition-colors ${
                    currentPage === 1 
                      ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed' 
                      : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  Previous
                </button>
                
                <div className="flex items-center gap-1">
                  {Array.from({ length: Math.min(totalPages, 5) }).map((_, i) => {
                    let pageToShow = i + 1;
                    if (totalPages > 5) {
                      if (currentPage <= 3) {
                        pageToShow = i + 1;
                      } else if (currentPage >= totalPages - 2) {
                        pageToShow = totalPages - 4 + i;
                      } else {
                        pageToShow = currentPage - 2 + i;
                      }
                    }
                    
                    return (
                      <button
                        key={pageToShow}
                        onClick={() => setCurrentPage(pageToShow)}
                        className={`px-3 py-1 rounded-md border text-sm font-medium transition-colors ${
                          currentPage === pageToShow
                            ? 'bg-brand-primary text-white border-brand-primary'
                            : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        {pageToShow}
                      </button>
                    );
                  })}
                </div>
                
                <button
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages}
                  className={`px-3 py-1 rounded-md border text-sm font-medium transition-colors ${
                    currentPage === totalPages
                      ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
                      : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}
      </div>

      <AddContactModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onAdd={handleAddContact}
        existingGroups={Array.from(new Set([...groups, ...contacts.flatMap(c => c.groups)]))}
      />

      <EditContactModal
        contact={editingContact}
        isOpen={editingContact !== null}
        onClose={() => setEditingContact(null)}
        onUpdate={(updatedContact) => {
          handleUpdateContact(updatedContact.id, updatedContact);
        }}
        existingGroups={Array.from(new Set([...groups, ...contacts.flatMap(c => c.groups)]))}
      />

      {selectedContact && (
        <ContactDetails
          contact={selectedContact}
          onClose={() => setSelectedContact(null)}
          onDelete={handleDeleteContact}
          onUpdate={async (updatedContactFromDetails) => {
            console.log('[ContactList.onUpdate] Received updatedContact from ContactDetails for general update:', updatedContactFromDetails);
            const finalUpdatedContact = await handleUpdateContact(updatedContactFromDetails.id, updatedContactFromDetails);
            if (finalUpdatedContact) {
              setSelectedContact(finalUpdatedContact);
            } else {
              console.error("[ContactList.onUpdate] handleUpdateContact failed, not updating selectedContact.");
            }
          }}
          onTodoChange={() => handleTodoChange(selectedContact.id)}
          existingGroups={Array.from(new Set([...groups, ...contacts.flatMap(c => c.groups)]))}
        />
      )}
    </div>
  );
};

export default ContactList;